import { defineAnalyticsEvent } from '@heidi/runtime/shell';

import type {
  CoachMarkEventProps,
  DemoCompletedEventProps,
  DemoStartedEventProps,
} from '../contracts/demo-flow-analytics.types';
import type {
  RemoteIntroPromoClickedEventProps,
  RemoteIntroPromoDismissedEventProps,
  RemoteIntroPromoViewedEventProps,
} from '../contracts/remote-intro-promo-analytics.types';

/**
 * The seven analytics events the growth domain owns outright, and the single
 * declaration site for each of their names (D9 ruling 27).
 *
 * FROZEN. Each `name` is the key a warehouse schema and its dashboards are built
 * on, so a rename is a new event and a second spelling silently splits a metric.
 * The `remote_intro_promo_*` trio is one campaign's funnel measured three ways —
 * a rename of any one of them makes the click-through rate divide by a
 * denominator that stopped being reported.
 *
 * `builds` is omitted throughout, i.e. `cloud-only` (D6). The on-prem appliance
 * ships without the SaaS analytics SDKs, so an event that reached the on-prem
 * registry would be a declaration nothing can emit. The promo half is doubly so:
 * `useIsRemoteIntroPromoEligible` short-circuits on `isOnPrem`, so on-prem never
 * reaches an emit anyway.
 *
 * WHY SEVEN AND NOT TEN. The four growth roots report ten distinct
 * `TrackingEventName` members. The other three — the mobile-download modal's
 * `modal_viewed` and the scribe-layout CSAT popover's `survey_viewed` /
 * `feedback_submitted` — are the app's CROSS-DOMAIN generic events. `modal_viewed`
 * has forty call sites across nine domains and `feedback_submitted` eight, so no
 * domain owns either and no domain may declare one: a name is globally unique and
 * the second declarer is a collision (`duplicate-event-name` in
 * `buildAppRegistry`, and the event-reference gate's third arm). What growth owns
 * of those three is their `screen_name` / `view_action` / `target_type`
 * vocabulary, which is in `../contracts/*-analytics.types.ts` with the payloads
 * and is bound to the app enum by a token map the emit host asserts. The same
 * split puts the practice-invite flow's `source` tokens in
 * `../contracts/team-invite-analytics.types.ts` with no descriptor here.
 *
 * WHAT IS NOT HERE: the emit, and the routing. A package declares its events and
 * never imports a tracker (ruling 24) — the `track()` calls and the
 * `{ amplitude: false }` routing stay app-side in
 * `src/models/analytics/features/`, keyed by the literal names these descriptors
 * carry. Nor can those hosts read `descriptor.name`: `event_name` discriminates
 * the app's `TrackingEventV2` union, whose members are string ENUM members, and
 * TypeScript does not accept a string literal in an enum member's place. All
 * seven names are already `TrackingEventName` members, so each host keeps the
 * enum member and its suite asserts the two agree — which is what makes the enum
 * a binding rather than a second spelling.
 */
export const remoteIntroPromoViewed =
  defineAnalyticsEvent<RemoteIntroPromoViewedEventProps>()({
    name: 'remote_intro_promo_viewed',
    description:
      'The Heidi Remote intro promo was shown on the billing banner, profile menu or modal',
  });

export const remoteIntroPromoClicked =
  defineAnalyticsEvent<RemoteIntroPromoClickedEventProps>()({
    name: 'remote_intro_promo_clicked',
    description: "The Heidi Remote intro promo's primary CTA was clicked",
  });

export const remoteIntroPromoDismissed =
  defineAnalyticsEvent<RemoteIntroPromoDismissedEventProps>()({
    name: 'remote_intro_promo_dismissed',
    description: 'A Heidi Remote intro promo surface was dismissed',
  });

export const demoStarted = defineAnalyticsEvent<DemoStartedEventProps>()({
  name: 'demo_started',
  description: 'The desktop demo session was started from the entry-point overlay',
});

export const demoCompleted = defineAnalyticsEvent<DemoCompletedEventProps>()({
  name: 'demo_completed',
  description: 'The demo coach-mark tour reached its end or was dismissed',
});

export const coachMarkViewed = defineAnalyticsEvent<CoachMarkEventProps>()({
  name: 'coach_mark_viewed',
  description: 'A demo coach-mark step was shown',
});

export const coachMarkActionTaken = defineAnalyticsEvent<CoachMarkEventProps>()({
  name: 'coach_mark_action_taken',
  description: 'A demo coach-mark step was advanced past',
});
